import { useMemo } from 'react';

/* eslint-disable react/no-unstable-nested-components */

const BreathingSquareCapsules = ({ 
  phase, 
  progress, 
  phaseDurations = { inhale: 4, hold: 4, exhale: 4, 'hold-out': 4 },
  colors = {
    inhale: '#22d3ee',
    hold: '#a855f7',
    exhale: '#3b82f6',
    'hold-out': '#a855f7',
    ready: '#94a3b8'
  },
  centerTimer = null // Timer to display in center
}) => {
  // Current color based on phase
  const currentColor = colors[phase] || colors.ready;
  
  // Secondary color for gradient - More dramatic contrast
  const getSecondaryColor = (phaseType) => {
    if (phaseType === 'inhale') return '#0369a1';      // Bleu profond
    if (phaseType === 'exhale') return '#b91c1c';       // Rouge profond
    if (phaseType === 'hold') return '#047857';         // Vert profond
    if (phaseType === 'hold-out') return '#4c1d95';     // Indigo profond
    return '#334155';
  };

  // Calculate fill levels for each capsule (progress is 0-100, convert to 0-1)
  const normalizedProgress = progress / 100;
  
  const capsuleFills = useMemo(() => {
    const fills = {
      left: 0,      // Inhale - vertical, fills bottom to top
      top: 0,       // Hold - horizontal, fills left to right
      right: 0,     // Exhale - vertical, fills top to bottom
      bottom: 0     // Hold-out - horizontal, fills right to left
    };

    if (phase === 'ready') {
      return fills;
    }

    const prog = progress / 100;

    if (phase === 'inhale') {
      fills.left = prog;
    } else if (phase === 'hold') {
      fills.left = 1;
      fills.top = prog;
    } else if (phase === 'exhale') {
      fills.left = 1;
      fills.top = 1;
      fills.right = prog;
    } else if (phase === 'hold-out') {
      fills.left = 1;
      fills.top = 1;
      fills.right = 1;
      fills.bottom = prog;
    }

    return fills;
  }, [phase, progress]);

  // Phase text
  const phaseText = useMemo(() => {
    if (phase === 'ready') return 'Prêt';
    if (phase === 'inhale') return 'Inspirez';
    if (phase === 'exhale') return 'Expirez';
    if (phase === 'hold' || phase === 'hold-out') return 'Retenez';
    return phase;
  }, [phase]);

  // Remaining time
  const remainingTime = phase !== 'ready' 
    ? Math.ceil((1 - progress / 100) * (phaseDurations[phase] || 0))
    : null;

  // Capsule component
  const Capsule = ({ orientation, position, fillLevel, phaseType, isActive }) => {
    const isVertical = orientation === 'vertical';
    const color = colors[phaseType] || colors.ready;
    const secondaryColor = getSecondaryColor(phaseType);
    
    // Determine fill direction
    const getFillStyle = () => {
      if (isVertical) {
        if (position === 'left') {
          // Inhale: fills from bottom to top
          return {
            height: `${fillLevel * 100}%`,
            bottom: 0,
            left: 0,
            right: 0,
            background: `linear-gradient(to top, ${secondaryColor}, ${color})`
          };
        } else {
          // Exhale: fills from top to bottom
          return {
            height: `${fillLevel * 100}%`,
            top: 0,
            left: 0,
            right: 0,
            background: `linear-gradient(to bottom, ${color}, ${secondaryColor})`
          };
        }
      } else {
        if (position === 'top') {
          // Hold: fills from left to right
          return {
            width: `${fillLevel * 100}%`,
            left: 0,
            top: 0,
            bottom: 0,
            background: `linear-gradient(to right, ${secondaryColor}, ${color})`
          };
        } else {
          // Hold-out: fills from right to left
          return {
            width: `${fillLevel * 100}%`,
            right: 0,
            top: 0,
            bottom: 0,
            background: `linear-gradient(to left, ${secondaryColor}, ${color})`
          };
        }
      }
    };

    // Larger capsule sizes
    const baseClasses = isVertical 
      ? "w-14 sm:w-16 lg:w-20 h-44 sm:h-56 lg:h-72 rounded-full" 
      : "w-44 sm:w-56 lg:w-72 h-14 sm:h-16 lg:h-20 rounded-full";

    return (
      <div className={`relative ${baseClasses} overflow-hidden bg-white/60 backdrop-blur-sm border-2 border-slate-600/70 shadow-xl transition-all duration-300 ${isActive ? 'ring-2 ring-slate-800/50' : ''}`}>
        {/* Background glow */}
        <div 
          className="absolute inset-0 opacity-20 transition-opacity duration-300"
          style={{
            background: isActive ? `radial-gradient(ellipse at center, ${color}60, transparent 70%)` : 'transparent'
          }}
        />
        
        {/* Fill */}
        <div 
          className="absolute transition-all duration-100 ease-out rounded-full"
          style={{
            ...getFillStyle(),
            boxShadow: fillLevel > 0 ? `0 0 20px ${color}50, inset 0 0 15px ${color}20` : 'none'
          }}
        >
          {/* Bubbles inside */}
          {fillLevel > 0.1 && [...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/30 animate-pulse"
              style={{
                width: `${3 + (i % 2) * 2}px`,
                height: `${3 + (i % 2) * 2}px`,
                left: isVertical ? `${20 + (i * 25)}%` : `${15 + (i * 30)}%`,
                top: isVertical ? `${20 + (i * 25)}%` : `${25 + (i * 15)}%`,
                animationDelay: `${i * 0.3}s`
              }}
            />
          ))}
          
          {/* Shine effect at the leading edge */}
          {fillLevel > 0 && fillLevel < 1 && (
            <div 
              className={`absolute bg-white/50 blur-sm ${
                isVertical 
                  ? (position === 'left' ? 'top-0 left-1/2 -translate-x-1/2 w-1/2 h-1' : 'bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-1')
                  : (position === 'top' ? 'right-0 top-1/2 -translate-y-1/2 w-1 h-1/2' : 'left-0 top-1/2 -translate-y-1/2 w-1 h-1/2')
              } rounded-full`}
            />
          )}
        </div>
        
        {/* Glass reflection */}
        <div 
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
          }}
        />
        
        {/* Side highlight */}
        <div 
          className={`absolute bg-white/10 blur-sm rounded-full ${
            isVertical 
              ? 'left-1 top-3 bottom-3 w-1.5' 
              : 'top-1 left-3 right-3 h-1.5'
          }`}
        />
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center">
      {/* Square arrangement of capsules - Much larger */}
      <div className="relative w-72 sm:w-80 lg:w-[420px] h-72 sm:h-80 lg:h-[420px]">
        {/* Top horizontal capsule (Hold) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          <Capsule 
            orientation="horizontal" 
            position="top" 
            fillLevel={capsuleFills.top}
            phaseType="hold"
            isActive={phase === 'hold'}
          />
        </div>
        
        {/* Bottom horizontal capsule (Hold-out) */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
          <Capsule 
            orientation="horizontal" 
            position="bottom" 
            fillLevel={capsuleFills.bottom}
            phaseType="hold-out"
            isActive={phase === 'hold-out'}
          />
        </div>
        
        {/* Left vertical capsule (Inhale) */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2">
          <Capsule 
            orientation="vertical" 
            position="left" 
            fillLevel={capsuleFills.left}
            phaseType="inhale"
            isActive={phase === 'inhale'}
          />
        </div>
        
        {/* Right vertical capsule (Exhale) */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          <Capsule 
            orientation="vertical" 
            position="right" 
            fillLevel={capsuleFills.right}
            phaseType="exhale"
            isActive={phase === 'exhale'}
          />
        </div>
        
        {/* Center indicator - Timer or small dot */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {centerTimer !== null ? (
            <div className="bg-slate-900/75 backdrop-blur-md rounded-2xl px-4 py-2 shadow-lg border border-slate-700/40">
              <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white" style={{ fontFamily: 'system-ui' }}>
                {String(Math.floor(centerTimer / 60)).padStart(2, '0')}:{String(centerTimer % 60).padStart(2, '0')}
              </span>
            </div>
          ) : (
            <div 
              className="w-3 h-3 rounded-full transition-all duration-300"
              style={{
                backgroundColor: phase === 'ready' ? 'rgba(255,255,255,0.3)' : currentColor,
                boxShadow: phase !== 'ready' ? `0 0 15px ${currentColor}` : 'none'
              }}
            />
          )}
        </div>
      </div>

      {/* Phase indicator */}
      <div className="text-center mt-8 sm:mt-10">
        <span 
          className="text-5xl sm:text-6xl lg:text-7xl font-bold drop-shadow-lg transition-colors duration-300"
          style={{ color: currentColor }}
        >
          {phaseText}
        </span>
        {remainingTime !== null && (
          <span className="text-3xl sm:text-4xl lg:text-5xl text-slate-700 ml-4">
            {remainingTime}s
          </span>
        )}
      </div>
      
      {/* Progress indicator showing which phase we're in */}
      <div className="flex gap-2 mt-4">
        {['inhale', 'hold', 'exhale', 'hold-out'].map((p) => (
          <div 
            key={p}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              phase === p ? 'scale-125' : 'opacity-50'
            }`}
            style={{ 
              backgroundColor: phase === p ? colors[p] : 'rgba(255,255,255,0.3)',
              boxShadow: phase === p ? `0 0 10px ${colors[p]}` : 'none'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default BreathingSquareCapsules;
